import { Input } from "@components/shadcn-ui/input";
import { useState, KeyboardEvent, useEffect, useRef } from "react";

interface UserSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  loginHistoryData: any[];
}

export function UserSearchInput({
  value,
  onChange,
  loginHistoryData,
}: UserSearchInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get unique userIds from loginHistoryData
  const suggestions = Array.from(
    new Set(
      loginHistoryData
        .map((item: any) => item.userId)
        .filter((userId: string) => {
          const normalizedInput = value.toLowerCase();
          const normalizedUserId = userId.toLowerCase();
          return (
            normalizedUserId.includes(normalizedInput) &&
            normalizedUserId !== normalizedInput
          );
        })
    )
  ).slice(0, 5);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : -1
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > -1 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          onChange(suggestions[selectedIndex]);
          setShowSuggestions(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      case "Tab":
        if (showSuggestions) {
          e.preventDefault();
          if (selectedIndex >= 0) {
            onChange(suggestions[selectedIndex]);
          }
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full flex-1">
      <Input
        ref={inputRef}
        type="text"
        placeholder="Search by username"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setSelectedIndex(-1);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        className="w-full flex-1 h-11"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 w-full bg-white mt-1 border rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion: string, index: number) => (
            <div
              key={suggestion}
              className={`px-4 py-2 cursor-pointer ${
                index === selectedIndex ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
              onClick={() => {
                onChange(suggestion);
                setShowSuggestions(false);
                setSelectedIndex(-1);
                inputRef.current?.blur();
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
